/**
 * ClarityLoader Component
 *
 * Loads Microsoft Clarity (session recordings, heatmaps, scroll depth) only
 * once the visitor's Analytics consent is on. The starting value comes from
 * the region defaults in geolocationService: on outside the EU, opt-in inside
 * it — so this is the one tracker that actually honours the cookie banner.
 *
 * Clarity follows SPA route changes on its own; no per-route call is needed.
 *
 * A script cannot be unloaded, so revoking consent after load calls
 * clarity("consent", false), which stops recording and clears Clarity's
 * cookies. Granting it again calls clarity("consent").
 */

import { useEffect } from "react";
import { useConsent } from "../../context/ConsentContext";

const CLARITY_PROJECT_ID = "yrhmdzsvbv";
const SCRIPT_ID = "ms-clarity";

const ClarityLoader: React.FC = () => {
  const { consent, isLoading } = useConsent();

  useEffect(() => {
    if (isLoading) return;

    try {
      const w = window as any;

      // Never inject under the prerenderers (prerender.js at build time,
      // prerender-blog.js on the VPS — both headless puppeteer, so
      // navigator.webdriver is true). Their snapshot would freeze the <script>
      // into static HTML, and it would then load for every visitor whatever
      // they chose — which is exactly what shipped on 2026-10-03.
      if (navigator.webdriver) return;

      if (!consent.analytics) {
        if (typeof w.clarity === "function") w.clarity("consent", false);
        return;
      }

      if (document.getElementById(SCRIPT_ID)) {
        w.clarity("consent");
        return;
      }

      // Official snippet: queue calls until the tag loads.
      w.clarity =
        w.clarity ||
        function () {
          // eslint-disable-next-line prefer-rest-params
          (w.clarity.q = w.clarity.q || []).push(arguments);
        };
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;
      document.head.appendChild(script);
    } catch {
      // never let analytics break the app
    }
  }, [consent.analytics, isLoading]);

  return null;
};

export default ClarityLoader;
